import { inject } from '@angular/core';
import { Router, CanActivateFn } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { ApiService } from '../services/api.service';
import { NAVIGATION_PERMISSIONS } from '../constants/groups.constants';
import { combineLatest, firstValueFrom, of } from 'rxjs';
import { filter, take, catchError } from 'rxjs/operators';

export const authGuard: CanActivateFn = async (route, state) => {
  const authService = inject(AuthService);
  const apiService = inject(ApiService);
  const router = inject(Router);

  const [, initialIsAuth] = await firstValueFrom(
    combineLatest([authService.isInitialized$, authService.isAuthenticated$]).pipe(
      filter(([isInitialized]) => isInitialized),
      take(1)
    )
  );

  let isAuth = initialIsAuth;

  // Re-check once to avoid false redirect during refresh/auth-settle window
  if (!isAuth) {
    await new Promise((resolve) => setTimeout(resolve, 200));
    isAuth = !!authService.getActiveAccount();
  }

  if (!isAuth) {
    router.navigate(['/welcome'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  // Check role-based permissions
  const routePath = route.routeConfig?.path || '';
  const allowedRoles = NAVIGATION_PERMISSIONS[routePath];

  if (!allowedRoles) {
    return true;
  }

  // Load userRoles if needed (getUserRolesOnce caches so no duplicate HTTP call)
  if (apiService.userRoles.length === 0) {
    await firstValueFrom(apiService.getUserRolesOnce().pipe(catchError(() => of([]))));
  }

  // Check access
  const userGroups = authService.getUserGroups();
  const userRoles = apiService.userRoles
    .filter((role) => userGroups.includes(role.userRoleObjectId))
    .map((role) => role.userRoleName.substring(0, role.userRoleName.lastIndexOf('.')));

  const hasAccess = allowedRoles.some((allowedRole) =>
    userRoles.some((userRole) => userRole.toLowerCase() === allowedRole.toLowerCase())
  );

  if (!hasAccess) {
    router.navigate(['/welcome'], { queryParams: { returnUrl: state.url } });
    return false;
  }

  return true;
};
